import { Link, useSearchParams } from 'react-router-dom';
import { chineseRestaurants } from '../../mocks/restaurants';
import { useState, useEffect } from 'react';

export default function ChineseRestaurants() {
  const [searchParams] = useSearchParams();
  const [startIndex, setStartIndex] = useState(0);
  const [glowingRestaurant, setGlowingRestaurant] = useState<number | null>(null);
  const itemsPerPage = 6;
  const displayedRestaurants = chineseRestaurants.slice(startIndex, startIndex + itemsPerPage);
  const hasMore = startIndex + itemsPerPage < chineseRestaurants.length;

  useEffect(() => {
    const restaurantId = searchParams.get('restaurant');
    if (restaurantId) {
      const id = parseInt(restaurantId);
      setGlowingRestaurant(id);
      
      // Scroll to the restaurant
      setTimeout(() => {
        const element = document.getElementById(`restaurant-${id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      // Remove glow after 2 seconds
      setTimeout(() => {
        setGlowingRestaurant(null);
      }, 2000);
    }
  }, [searchParams]);

  const handleLoadMore = () => {
    if (hasMore) {
      setStartIndex(prev => prev + itemsPerPage);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b-4 border-black z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://public.readdy.ai/ai/img_res/46496767-ecf9-45a1-9f97-898f0428276b.png" 
              alt="Antalya Eats Logo" 
              className="w-8 h-8 object-contain image-rendering-pixelated"
              style={{ imageRendering: 'pixelated' }}
            />
            <span className="text-lg font-bold tracking-tight">ANTALYA EATS</span>
          </Link>
          <nav className="flex gap-4">
            <Link to="/" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">Home</Link>
            <Link to="/about" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">About</Link>
          </nav>
        </div>
      </header>

      {/* Page Header */}
      <section className="pt-20 pb-8 px-4 bg-yellow-400 border-b-4 border-black">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/218960c6-7669-47d0-b2cf-b28d21b21682.png" 
              alt="Chinese" 
              className="w-16 h-16 object-contain brightness-0 invert"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">CHINESE RESTAURANTS</h1>
          <p className="text-base text-white">Experience authentic Chinese flavors in Antalya</p>
        </div>
      </section>

      {/* Restaurants List */}
      <section className="py-8 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid gap-4">
            {chineseRestaurants.map((restaurant) => (
              <div 
                key={restaurant.id}
                id={`restaurant-${restaurant.id}`}
                className={`bg-white border-4 border-black p-4 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all ${
                  glowingRestaurant === restaurant.id ? 'animate-glow' : ''
                }`}
                style={glowingRestaurant === restaurant.id ? {
                  boxShadow: '0 0 20px 5px rgba(250, 204, 21, 0.8), 8px 8px 0px 0px rgba(0,0,0,1)',
                  animation: 'glow 0.5s ease-in-out 4'
                } : undefined}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">{restaurant.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{restaurant.description}</p>
                    <div className="flex flex-col gap-2 text-xs">
                      <div className="flex items-center gap-2">
                        <i className="ri-map-pin-line text-base"></i>
                        <span>{restaurant.address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-phone-line text-base"></i>
                        <a href={`tel:${restaurant.phone}`} className="hover:text-yellow-500 transition-colors cursor-pointer">
                          {restaurant.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-400 text-lg"></i>
                      <span className="text-lg font-bold">{restaurant.rating}</span>
                    </div>
                    <div className="text-base font-bold text-yellow-500">{restaurant.priceRange}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="mt-8 flex justify-center gap-4">
            <Link 
              to="/"
              className="inline-block bg-yellow-400 hover:bg-yellow-500 text-white font-bold text-base px-6 py-3 border-4 border-black transition-colors whitespace-nowrap cursor-pointer"
            >
              ← BACK TO CATEGORIES
            </Link>
            {hasMore && (
              <button
                onClick={handleLoadMore}
                className="inline-block bg-yellow-400 hover:bg-yellow-500 text-white font-bold text-base px-6 py-3 border-4 border-black transition-colors whitespace-nowrap cursor-pointer"
              >
                MORE →
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 border-t-4 border-black">
        <div className="container mx-auto text-center">
          <p className="text-sm mb-2">© 2025 Antalya Eats. Your guide to the best restaurants in Antalya.</p>
          <a href="https://readdy.ai/?ref=logo" className="text-xs text-gray-400 hover:text-white whitespace-nowrap">Powered by Readdy</a>
        </div>
      </footer>
    </div>
  );
}
